import React, { useEffect, useRef, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ChevronDown, Play } from 'lucide-react';
import { Button } from './ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const mediaUrl = (url) => (url && url.startsWith('/api') ? `${API_URL}${url}` : url);

/**
 * Nur direkte Videodateien übernehmen. Im Feld kann noch ein alter
 * YouTube-Link stehen – der würde als <source> nicht funktionieren.
 */
const videoFile = (url) => {
  if (!url) return null;
  if (!/\.(mp4|webm|mov)(\?|$)/i.test(url)) return null;
  return mediaUrl(url);
};

const imageFile = (url) => {
  if (!url) return null;
  if (!/\.(jpe?g|png|webp|avif)(\?|$)/i.test(url)) return null;
  return mediaUrl(url);
};

/**
 * Hero mit selbst gehostetem Hintergrundvideo (kein YouTube-Player,
 * dadurch keine fremden Bedienelemente/Branding am Anfang).
 * Querformat für Desktop, Hochformat für Mobil.
 */
export default function HeroSection({ content, settings = {} }) {
  const reduceMotion = useReducedMotion();
  const videoRef = useRef(null);
  const [isPortrait, setIsPortrait] = useState(
    typeof window !== 'undefined' ? window.matchMedia('(max-width: 767px)').matches : false
  );

  const headline = content?.hero_headline || 'WIR GEBEN MARKEN EINE RICHTUNG.';
  const subtext = content?.hero_subtext || 'Foto, Video, Analyse und Social Media als ganzheitliche Produktion, strategisch gedacht und präzise umgesetzt.';
  const ctaText = content?.hero_cta || 'Projekt starten';
  const showHeroText = settings?.show_hero_text ?? false;

  // Im Admin hinterlegte Videos haben Vorrang, sonst die mitgelieferten Dateien
  const landscapeSrc = videoFile(settings?.hero_video_url) || '/video/hero-landscape.mp4';
  const portraitSrc = videoFile(settings?.hero_video_portrait_url) || '/video/hero-portrait.mp4';
  const landscapePoster = imageFile(settings?.hero_poster_url) || '/video/hero-landscape.jpg';
  const portraitPoster = imageFile(settings?.hero_poster_portrait_url) || '/video/hero-portrait.jpg';

  const videoSrc = isPortrait ? portraitSrc : landscapeSrc;
  const poster = isPortrait ? portraitPoster : landscapePoster;

  // Bei Größen- oder Ausrichtungswechsel die passende Fassung laden.
  // Zusätzlich zu matchMedia auch auf resize hören – manche Umgebungen
  // (Geräte-Emulation, Orientierungswechsel) lösen "change" nicht aus.
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const sync = () => setIsPortrait(mq.matches);

    sync();
    mq.addEventListener('change', sync);
    window.addEventListener('resize', sync);
    window.addEventListener('orientationchange', sync);
    return () => {
      mq.removeEventListener('change', sync);
      window.removeEventListener('resize', sync);
      window.removeEventListener('orientationchange', sync);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || reduceMotion) return;

    // load() bricht ein laufendes play() ab -> nach "canplay" erneut versuchen
    const tryPlay = () => {
      const play = video.play();
      if (play?.catch) play.catch(() => {}); // Blockade ignorieren, Poster bleibt sichtbar
    };

    video.load();
    tryPlay();
    video.addEventListener('canplay', tryPlay);
    return () => video.removeEventListener('canplay', tryPlay);
  }, [videoSrc, reduceMotion]);

  const scrollToContent = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  const lines = headline.split('\n').map((l) => l.trim()).filter(Boolean);

  return (
    <section className="relative h-screen w-full overflow-hidden bg-ink">
      {/* Hintergrundvideo */}
      <video
        ref={videoRef}
        className="absolute inset-0 w-full h-full object-cover"
        poster={poster}
        autoPlay={!reduceMotion}
        muted
        loop
        playsInline
        preload="metadata"
        aria-hidden="true"
        tabIndex={-1}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>

      {/* Overlay */}
      <div className={`absolute inset-0 ${showHeroText ? 'bg-gradient-to-t from-black/80 via-black/40 to-transparent' : 'bg-black/20'}`} />

      {/* Inhalt – nur wenn im Admin aktiviert */}
      {showHeroText && (
        <div className="relative z-10 h-full flex flex-col justify-center container-custom">
          <div className="max-w-4xl">
            <motion.span
              initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
              className="kicker mb-8 inline-flex !text-white/70"
            >
              {content?.hero_kicker || 'Creative Production · Vienna'}
            </motion.span>

            <motion.h1
              initial={{ opacity: 0, y: 48, filter: 'blur(10px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.9, delay: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-8xl text-white leading-[0.88] mb-6"
            >
              {lines.map((line, i) => (
                <span key={i} className={`block ${i === lines.length - 1 ? 'text-yellow' : ''}`}>
                  {line}
                </span>
              ))}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="text-lg md:text-xl text-white/80 max-w-xl mb-10 leading-relaxed"
            >
              {subtext}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 30, filter: 'blur(8px)' }}
              animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
              transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="flex flex-wrap items-center gap-4"
            >
              <Button
                size="lg"
                className="btn-primary group"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                {ctaText}
                <motion.span
                  className="ml-2"
                  animate={reduceMotion ? {} : { x: [0, 4, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  →
                </motion.span>
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="btn-outline !text-white !border-white hover:!bg-white hover:!text-ink group"
                onClick={() => document.getElementById('portfolio')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Play className="w-4 h-4 mr-2" />
                Arbeiten ansehen
              </Button>
            </motion.div>
          </div>
        </div>
      )}

      {/* Gelbe Markenkante unten */}
      <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-yellow z-10" />

      {/* Scroll-Hinweis */}
      <motion.button
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        onClick={scrollToContent}
        className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center text-white/70 hover:text-white transition-colors"
        aria-label="Nach unten scrollen"
      >
        <span className="text-xs uppercase tracking-widest mb-2">Scroll</span>
        <motion.div
          animate={reduceMotion ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <ChevronDown className="w-5 h-5" />
        </motion.div>
      </motion.button>
    </section>
  );
}
